import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * Para onde vai quem confirma «Sair mesmo assim» a partir de «Salvar e sair» (fora do fluxo do contrato).
 * Mesmo site institucional já usado pelo `CookieConsent`.
 */
export const CONTRACT_EXIT_URL = 'https://www.aride.com.br';

export function leaveContractJourney() {
  window.location.assign(CONTRACT_EXIT_URL);
}

type ContractExitGuardValue = {
  /** Abre o aviso «Lembre-se de que somente finalizamos a contratação…»; `onConfirm` corre em «Sair mesmo assim». */
  requestExit: (onConfirm?: () => void) => void;
};

export const ContractExitGuardContext = createContext<ContractExitGuardValue | null>(null);

export function useContractExitGuardContext() {
  return useContext(ContractExitGuardContext);
}

/**
 * Guarda de saída dos passos do contrato (pós-pagamento): enquanto montado, fechar/recarregar a aba dispara o
 * diálogo nativo do navegador (`beforeunload` — o texto é do navegador, não dá para mostrar o popup próprio).
 * Saídas dentro da app («Salvar e sair», «Voltar» do passo 1) passam por `requestExit`, que abre o popup.
 *
 * Pendência de back: o Figma pede também um e-mail com o link de recuperação ao sair; o api-core não tem
 * endpoint para disparar esse e-mail sob demanda, por isso «Sair mesmo assim» só sai.
 */
export function useContractExitGuard(onLeave: () => void) {
  const [open, setOpen] = useState(false);
  const pendingExit = useRef<(() => void) | null>(null);
  const released = useRef(false);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (released.current) return;
      event.preventDefault();
      // Chrome/Edge antigos exigem `returnValue` para mostrar o diálogo.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const requestExit = useCallback((onConfirm?: () => void) => {
    pendingExit.current = onConfirm ?? null;
    setOpen(true);
  }, []);

  const stay = useCallback(() => {
    pendingExit.current = null;
    setOpen(false);
  }, []);

  const confirmExit = useCallback(() => {
    const action = pendingExit.current ?? onLeave;
    pendingExit.current = null;
    released.current = true;
    setOpen(false);
    action();
  }, [onLeave]);

  return { open, requestExit, stay, confirmExit };
}
